/**
 * TableHistoricoContent — renders the HISTÓRICO tab inside TableAccountContent.
 * Fetches closed account history from backend; payment rows are clickable to open ClosedAccountModal.
 */
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { formatPrice } from "@/utils/currency";
import { Badge } from "@/components/ui/badge";
import { ClosedAccountModal } from "./ClosedAccountModal";

export function TableHistoricoContent({ user, tableId }) {
  const { t } = useTranslation();
  const { authAxios } = useAuth();
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedPaymentOrders, setSelectedPaymentOrders] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const fetchHistory = async () => {
      if (!tableId) {
        setLoading(false);
        return;
      }
      try {
        const response = await authAxios.get(`/tables/${tableId}/history`);
        setHistoryData(response.data || []);
      } catch (error) {
        console.error("Failed to fetch table history:", error);
        setHistoryData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [tableId, authAxios]);

  const handlePaymentClick = (payment, paymentOrders) => {
    setSelectedPayment(payment);
    setSelectedPaymentOrders(paymentOrders);
    setModalOpen(true);
  };

  const allItems = [];
  historyData.forEach(session => {
    if (session.orders) {
      session.orders.forEach(order => {
        allItems.push({ type: "order", data: order });
      });
    }
    if (session.payments) {
      session.payments.forEach(payment => {
        allItems.push({ type: "payment", data: payment, session });
      });
    }
  });

  allItems.sort((a, b) => {
    const timeA = a.data.created_at || "";
    const timeB = b.data.created_at || "";
    return new Date(timeB) - new Date(timeA);
  });

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-400">
        Carregando histórico...
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100 text-gray-500 text-xs uppercase">
            <th className="text-left p-3 font-medium">Fecha y hora</th>
            <th className="text-left p-3 font-medium">Canal</th>
            <th className="text-left p-3 font-medium">Tipo</th>
            <th className="text-left p-3 font-medium">Descrição</th>
            <th className="text-left p-3 font-medium">Status</th>
            <th className="text-left p-3 font-medium">Responsável</th>
            <th className="text-right p-3 font-medium">Valor</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {allItems.map((item, idx) => {
            if (item.type === "order") {
              const order = item.data;
              return (
                <tr key={`ord-${order.id}`} className="hover:bg-gray-50">
                  <td className="p-3 text-gray-600">{order.created_at ? new Date(order.created_at).toLocaleString("es-ES", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "-"}</td>
                  <td className="p-3">
                    <Badge className={order.source === "pos" ? "bg-blue-100 text-blue-700" : order.source === "qr" || order.source === "table" ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-600"}>
                      {order.source === "pos" ? "PDV" : order.source === "qr" || order.source === "table" ? "QR Code" : order.source || "Online"}
                    </Badge>
                  </td>
                  <td className="p-3 font-medium">Pedido</td>
                  <td className="p-3 text-gray-600">#{order.id.slice(-6).toUpperCase()} - {order.items?.length || 0} items</td>
                  <td className="p-3">
                    <Badge className={
                      order.status === "delivered" ? "bg-emerald-100 text-emerald-700" :
                      order.status === "pending_payment" ? "bg-red-100 text-red-700 border border-red-200" :
                      order.status === "pending" ? "bg-yellow-100 text-yellow-700" :
                      "bg-gray-100 text-gray-600"
                    }>
                      {order.status === "delivered" ? "Finalizado" :
                       order.status === "pending_payment" ? "⚠ Aguarda Pagamento" :
                       order.status === "pending" ? "Pendente" :
                       order.status || "Em curso"}
                    </Badge>
                  </td>
                  <td className="p-3 text-gray-600">{order.created_by || user?.name || "Sistema"}</td>
                  <td className="p-3 text-right font-bold">{formatPrice(order.total)}</td>
                </tr>
              );
            } else {
              const payment = item.data;
              const paymentOrders = payment.orders || [];
              return (
                <tr
                  key={`pay-${payment.id}`}
                  className="hover:bg-emerald-50 bg-emerald-25 cursor-pointer"
                  onClick={() => handlePaymentClick(payment, paymentOrders)}
                >
                  <td className="p-3 text-gray-600">{payment.created_at ? new Date(payment.created_at).toLocaleString("es-ES", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "-"}</td>
                  <td className="p-3"><Badge className="bg-emerald-100 text-emerald-700">PDV</Badge></td>
                  <td className="p-3 font-medium text-emerald-700">Pagamento</td>
                  <td className="p-3 text-gray-600">{payment.method === "cash" ? "Dinheiro" : payment.method === "card" ? `Cartão${payment.card_type ? ` (${payment.card_type})` : ""}` : payment.method}</td>
                  <td className="p-3"><Badge className="bg-emerald-100 text-emerald-700">{payment.status === "paid" ? "Confirmado" : "Pendente"}</Badge></td>
                  <td className="p-3 text-gray-600">{payment.created_by || user?.name || "Sistema"}</td>
                  <td className="p-3 text-right font-bold text-emerald-700">{formatPrice(payment.amount)}</td>
                </tr>
              );
            }
          })}
          {allItems.length === 0 && (
            <tr><td colSpan={7} className="p-8 text-center text-gray-400">Nenhuma atividade registrada</td></tr>
          )}
        </tbody>
      </table>

      {selectedPayment && (
        <ClosedAccountModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          payment={selectedPayment}
          orders={selectedPaymentOrders}
          tableId={tableId}
        />
      )}
    </div>
  );
}
